import { TFunction } from "react-i18next";
import isEmail from "utils/isEmail";

export type MapCSVRow<T> = (
  rowValues: string[],
  error: ErrorHandler,
  t: TFunction
) => Promise<void | T>;

type ErrorHandler = {
  (errorCode: string, column?: number): void;
  some: () => boolean;
};

export type RowError = { row: number; column?: number; error: string };
export type ParseResult<T> = { items: T[]; errors: RowError[] };

function Errors() {
  const errors: RowError[] = [];

  return {
    byIndex(index: number) {
      const error = (errorMsg: string, column?: number) => {
        errors.push({ row: index, column, error: errorMsg });
      };

      error.some = () => errors.length > 0;
      return error;
    },

    all() {
      return errors;
    },
  };
}

export async function parseContent<T>(
  csvContent: string,
  mapRow: MapCSVRow<T>,
  t: TFunction
): Promise<ParseResult<T>> {
  const rows = csvContent.split("\n");
  const errors = Errors();

  const items = await rows.reduce(async (acc, row, index) => {
    const items = await acc;
    const values = row.split(",");
    if (!row.trim()) return acc;

    // Skip header, if the file has one (all formats start with the email)
    if (index === 0 && !isEmail(values[0].trim())) return acc;

    const result = await mapRow(values, errors.byIndex(index), t);
    if (result) items.push(result);

    return acc;
  }, Promise.resolve([]) as Promise<T[]>);

  return { items, errors: errors.all() };
}

export async function extractFromFiles(files: File[]): Promise<string[]> {
  const extractionPromises = await files.map<Promise<string>>(file => {
    return new Promise(resolve => {
      if (!file) resolve(null);
      const reader = new FileReader();
      reader.readAsText(file);
      reader.onload = e => {
        resolve(e.target.result as string);
      };
    });
  });

  return Promise.all(extractionPromises);
}
